import React, { useState } from 'react';
import { Box, Typography, Grid, FormControlLabel, Checkbox, TextField } from '@mui/material';
import { FormComponentProps, PriorTaxInfoData } from '../../types/forms';

const PriorTaxInfo: React.FC<FormComponentProps<PriorTaxInfoData>> = ({ data, onNext }) => {
  const [formData, setFormData] = useState({
    priorYearAGI: data?.priorYearAGI || 0,
    priorYearTaxPaid: data?.priorYearTaxPaid || 0,
    priorYearRefund: data?.priorYearRefund || 0,
    priorYearDeductions: data?.priorYearDeductions || 0,
    irsPin: '',
    hasIRSNotices: false,
    hasCarryforward: false,
    hasNameChange: false,
    hasStateMoved: false,
    hasEstimatedPayments: false,
  });

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const newData = {
      ...formData,
      [field]: value
    };
    setFormData(newData);
    onNext({
      priorYearAGI: newData.priorYearAGI,
      priorYearTaxPaid: newData.priorYearTaxPaid,
      priorYearRefund: newData.priorYearRefund,
      priorYearDeductions: newData.priorYearDeductions
    });
  };

  return (
    <Box>
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 400 }}>
        Prior Tax Information
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph sx={{ fontWeight: 400 }}>
        Help us understand your tax history and any ongoing tax matters.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Prior Year AGI"
            type="number"
            value={formData.priorYearAGI}
            onChange={handleChange('priorYearAGI')}
            placeholder="0.00"
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Prior Year Tax Paid"
            type="number"
            value={formData.priorYearTaxPaid}
            onChange={handleChange('priorYearTaxPaid')}
            placeholder="0.00"
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Prior Year Refund"
            type="number"
            value={formData.priorYearRefund}
            onChange={handleChange('priorYearRefund')}
            placeholder="0.00"
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Prior Year Deductions"
            type="number"
            value={formData.priorYearDeductions}
            onChange={handleChange('priorYearDeductions')}
            placeholder="0.00"
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="IRS Identity Protection PIN (if issued)"
            value={formData.irsPin}
            onChange={handleChange('irsPin')}
            placeholder="Enter your IP PIN"
            margin="normal"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasIRSNotices}
                onChange={handleChange('hasIRSNotices')}
                color="primary"
              />
            }
            label={<Typography variant="h6" sx={{ fontWeight: 400 }}>I have received IRS letters or notices</Typography>}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasCarryforward}
                onChange={handleChange('hasCarryforward')}
                color="primary"
              />
            }
            label={<Typography variant="h6" sx={{ fontWeight: 400 }}>I have carryforward items (capital losses, NOL, etc.)</Typography>}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasNameChange}
                onChange={handleChange('hasNameChange')}
                color="primary"
              />
            }
            label={<Typography variant="h6" sx={{ fontWeight: 400 }}>I had a name change this year</Typography>}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasStateMoved}
                onChange={handleChange('hasStateMoved')}
                color="primary"
              />
            }
            label={<Typography variant="h6" sx={{ fontWeight: 400 }}>I moved to a new state</Typography>}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasEstimatedPayments}
                onChange={handleChange('hasEstimatedPayments')}
                color="primary"
              />
            }
            label={<Typography variant="h6" sx={{ fontWeight: 400 }}>I made estimated tax payments</Typography>}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PriorTaxInfo; 